import { Injectable } from '@angular/core';
import {
  CellDataOrValue,
  CellValue,
  ColumnRuleEngine,
  ConditionMatchType,
  EnhancedColumnDefinition,
  OrphanedRuleError,
  RuleCondition,
} from './types';

/**
 * RuleEvaluatorService
 *
 * Pure-function service for evaluating conditional column rules. Used by both Stage (design-time preview) and Orchestra
 * (runtime rendering).
 *
 * @example
 *   ```typescript
 *   const evaluator = inject(RuleEvaluatorService);
 *   const enabledMap = evaluator.evaluateRow(rowValues, columns);
 *   // enabledMap = { 'col-1': true, 'col-2': false, ... }
 *   ```;
 */
@Injectable({ providedIn: 'root' })
export class RuleEvaluatorService {
  /**
   * Evaluate all column rules for a single row and return which columns are enabled.
   *
   * @param rowValues - Current values in the row, keyed by column ID (supports both nested { value, linkId } and direct
   *   value)
   * @param columns - All column definitions (including rule configurations)
   * @returns Record of columnId → enabled (true = enabled, false = disabled)
   */
  evaluateRow(
    rowValues: Record<string, CellDataOrValue>,
    columns: EnhancedColumnDefinition[]
  ): Record<string, boolean> {
    const result: Record<string, boolean> = {};

    for (const column of columns) {
      if (!column.ruleEngine || column.ruleEngine.conditions.length === 0) {
        result[column.id] = true;
        continue;
      }

      result[column.id] = this.evaluateRuleEngine(column.ruleEngine, rowValues, columns);
    }

    return result;
  }

  /**
   * Detect circular dependencies in column rule configurations.
   *
   * @param columns - All column definitions to check
   * @returns Array of circular dependency chains (empty array = no cycles)
   */
  detectCircularDependencies(columns: EnhancedColumnDefinition[]): string[][] {
    const cycles: string[][] = [];
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const adjacency = this.buildDependencyGraph(columns);

    for (const columnId of adjacency.keys()) {
      if (!visited.has(columnId)) {
        const path: string[] = [];
        this.dfs(columnId, adjacency, visited, recursionStack, path, cycles);
      }
    }

    return cycles;
  }

  /**
   * Validate that all rule references point to existing columns.
   *
   * @param columns - All column definitions to validate
   * @returns Array of orphaned rule errors (empty = all references valid)
   */
  validateRuleReferences(columns: EnhancedColumnDefinition[]): OrphanedRuleError[] {
    const errors: OrphanedRuleError[] = [];
    const columnIds = new Set(columns.map((c) => c.id));

    for (const column of columns) {
      if (!column.ruleEngine) continue;

      for (const condition of column.ruleEngine.conditions) {
        if (!columnIds.has(condition.dependsOnColumn)) {
          errors.push({
            columnId: column.id,
            conditionId: condition.id,
            referencedColumnId: condition.dependsOnColumn,
            message: `Column "${column.label}" has a rule referencing non-existent column "${condition.dependsOnColumn}"`,
          });
        }
      }
    }

    return errors;
  }

  // --- Private Methods ---

  private evaluateRuleEngine(
    ruleEngine: ColumnRuleEngine,
    rowValues: Record<string, CellDataOrValue>,
    columns: EnhancedColumnDefinition[]
  ): boolean {
    const results = ruleEngine.conditions.map((condition) => this.evaluateCondition(condition, rowValues, columns));

    if (ruleEngine.logicalJoiner === 'AND') {
      return results.every((r) => r);
    }
    return results.some((r) => r);
  }

  private evaluateCondition(
    condition: RuleCondition,
    rowValues: Record<string, CellDataOrValue>,
    columns: EnhancedColumnDefinition[]
  ): boolean {
    const sourceColumn = columns.find((c) => c.id === condition.dependsOnColumn);

    // Extract value from nested structure { value, linkId } or use direct value for backward compatibility
    let rawCellData = rowValues[condition.dependsOnColumn] as unknown;
    let rawSourceValue: unknown;

    if (rawCellData && typeof rawCellData === 'object' && 'value' in rawCellData) {
      rawSourceValue = (rawCellData as { value: unknown }).value;
    } else {
      rawSourceValue = rawCellData;
    }

    let sourceValue = rawSourceValue as CellValue;

    if (sourceColumn?.inputType === 'checkbox') {
      if (Array.isArray(rawSourceValue)) {
        sourceValue = rawSourceValue.length > 0;
      } else if (typeof rawSourceValue === 'string') {
        const normalizedValue = rawSourceValue.trim().toLowerCase();
        sourceValue = normalizedValue !== '' && normalizedValue !== 'false';
      } else {
        sourceValue = rawSourceValue === true;
      }
    }
    return this.matchCondition(condition.matchType, sourceValue, condition.matchValues);
  }

  private matchCondition(matchType: ConditionMatchType, sourceValue: CellValue, matchValues: string[]): boolean {
    switch (matchType) {
      case 'is-checked':
        return sourceValue === true;

      case 'is-unchecked':
        return sourceValue === false || sourceValue === null || sourceValue === undefined;

      case 'equals':
        return matchValues.some((mv) => String(sourceValue) === mv);

      case 'not-equals':
        return !matchValues.some((mv) => String(sourceValue) === mv);

      case 'greater-than': {
        const numVal = Number(sourceValue);
        const threshold = Number(matchValues[0]);
        return !isNaN(numVal) && !isNaN(threshold) && numVal > threshold;
      }

      case 'less-than': {
        const numVal = Number(sourceValue);
        const threshold = Number(matchValues[0]);
        return !isNaN(numVal) && !isNaN(threshold) && numVal < threshold;
      }

      case 'contains':
        return typeof sourceValue === 'string' && sourceValue.includes(matchValues[0] ?? '');

      case 'is-empty':
        return sourceValue === null || sourceValue === undefined || sourceValue === '';

      case 'is-not-empty':
        return sourceValue !== null && sourceValue !== undefined && sourceValue !== '';

      default:
        return true;
    }
  }

  private buildDependencyGraph(columns: EnhancedColumnDefinition[]): Map<string, Set<string>> {
    const graph = new Map<string, Set<string>>();

    for (const column of columns) {
      if (!graph.has(column.id)) {
        graph.set(column.id, new Set());
      }
      if (column.ruleEngine) {
        for (const condition of column.ruleEngine.conditions) {
          graph.get(column.id)!.add(condition.dependsOnColumn);
          if (!graph.has(condition.dependsOnColumn)) {
            graph.set(condition.dependsOnColumn, new Set());
          }
        }
      }
    }

    return graph;
  }

  private dfs(
    node: string,
    graph: Map<string, Set<string>>,
    visited: Set<string>,
    recursionStack: Set<string>,
    path: string[],
    cycles: string[][]
  ): void {
    visited.add(node);
    recursionStack.add(node);
    path.push(node);

    const neighbors = graph.get(node) || new Set();
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        this.dfs(neighbor, graph, visited, recursionStack, path, cycles);
      } else if (recursionStack.has(neighbor)) {
        const cycleStart = path.indexOf(neighbor);
        if (cycleStart !== -1) {
          cycles.push([...path.slice(cycleStart), neighbor]);
        }
      }
    }

    path.pop();
    recursionStack.delete(node);
  }
}
